import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { FormBuilderBoard } from "./components/FormBuilderBoard/";
import { nanoid } from "nanoid";
import {
  Box,
  ChakraProvider,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  css,
  Text,
  useToast,
  useDisclosure,
  IconButton,
} from "@chakra-ui/react";

import {
  BuildChange,
  BuilderList,
  BuilderTabs,
  FormBuilderProps,
} from "./FormBuilderTypes";
import { buildList } from "../constants";
import { BuildersList } from "./components/BuildersList";
import { FormHeader } from "./components/FormHeader";
import { ConfirmationModal } from "./components/ConfirmationModal";
import { FormPreview } from "./components/FormPreview";
import { CloseIcon } from "../icons/CloseIcon";
import { PlusIcon } from "../icons/PlusIcon";

const FormBuilder = forwardRef(
  (
    { theme, onSubmit, defaultTabs, children, kyc = false }: FormBuilderProps,
    ref
  ) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [list, setList] = useState(buildList);
    const [minimumTab, setMinimumTab] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [tabs, setTab] = useState<BuilderTabs[]>(defaultTabs.length
        ? defaultTabs
      : [{ id: nanoid(), name: "New Form", builders: [], error: "", linkedToOption: false }]
    );

    const [current, setCurrent] = useState<any>({});
    const [order, setOrder] = useState(false);
    const toast = useToast();
    const [tabIndex, setTabIndex] = useState(0);
    const [error, setError] = useState<any>({});
    const [overTab, setOverTab] = useState<any>();

    useEffect(() => {
      if (error.msg) {
        toast({
          title: "Error",
          status: "error",
          duration: 4000,
          position: "top-right",
          description: error.msg,
        });
      }
    }, [error]);

    const validateDragDrop = (current: BuilderList, id: string) => {
      const currentBuilders = tabs.find((tab) => tab.id === id)?.builders;
      if (current.title === "Bank Info" || current.title === "Card") {
        if (currentBuilders && currentBuilders?.length > 0) {
          setError({
            msg: "Bank Info and Card cannot be combined with other components",
            type: "drop",
          });
          return true;
        }
      } else {
        if (
          currentBuilders?.filter(
            (build) => build.title === "Bank Info" || build.title === "Card"
          ).length
        ) {
          setError({
            msg: "Bank Info and Card cannot be combined with other components",
            type: "drop",
          });
          return true;
        }
      }
      return;
    };

    const togglePreview = () => {
      setPreviewOpen(!previewOpen);
    };

    const handleDragDrop = (id: string, current: BuilderList) => {
      //checking to make sure that Bank and card component are combined with other components
      const checkError = validateDragDrop(current, id);
      if (checkError) {
        return;
      }
      let newList = list?.filter((item) => item?.id !== current?.id);
      newList = [
        ...newList,
        {
          ...current,
          id: nanoid(),
          field1: current?.field1 || '',
          field2: current?.field2 || '',
          inputList: current?.inputList || [],
        },
      ].sort((a, b) => a.sort - b.sort);
    
      setList(newList);
    
      const newTab = tabs.map((tab) => {
        if (tab.id === id) {
          tab.builders = current
            ? [...tab.builders, current]
            : [...tab.builders];
        }
        return tab;
      });
    
      setTab(newTab);
    };

    const toggleReorder = () => {
      setOrder(!order);
    };
    const reOrderTabBuilders = (tabId: string, builder: BuilderList[]) => {
      const newTab = tabs.map((tab) => {
        if (tab.id === tabId) {
          tab.builders = builder;
        }
        return tab;
      });
      setTab(newTab);
    };

    const handleDragStart = (e: React.DragEvent, item: BuilderList) => {
      e.dataTransfer.setData("item", JSON.stringify(item));
    };

    const onDrop = (e: React.DragEvent, id: string) => {
      if (!order) {
        e.preventDefault();
        const current = JSON.parse(e.dataTransfer.getData("item"));
        const componentExist = tabs
          .find((tab) => tab.id === id)
          ?.builders.find((b) => b.id === current.id);
        if (!componentExist) {
          handleDragDrop(id, current);
          e.dataTransfer.clearData();
        }
      }
    };

    const handleDragEnter = (e: React.DragEvent, _id: string) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const validateTabs = (tabs: BuilderTabs[]) => {
      const builderCheck: any = tabs.reduce(
        (acc, tab: BuilderTabs, index: number) => {
          if (!tab.name || tab.name === "New Form") {
            const errorMsg = "Kindly provide a name for all tabs";
            acc = { error: errorMsg, tabIndex: index };
            const newTabs = [...tabs];
            newTabs[index] = { ...tab, error: errorMsg };

            setTab(newTabs);
            return acc;
          }
          if (tab.builders.length < 1) {
            const errorMsg = `Kindly drag atleast one component into Tab: ${tab.name}`;
            acc = {
              error: errorMsg,
              tabIndex: index,
            };
            return acc;
          }
          tab.builders.forEach((builder: BuilderList) => {
            if (!builder.instruction) {
              const errorMsg = `Tab: ${tab.name} as a form component without instruction, kindly input instruction for all components in ${tab.name}`;
              acc = {
                error: errorMsg,
                tabIndex: index,
              };
              const newTabs = handleBuildChange({
                buildId: builder.id,
                tabId: tab.id,
                value: errorMsg,
                key: "error",
              });

              setTab(newTabs);
              return acc;
            }
            if (builder?.type === "listInputs" &&(!builder?.field1 || !builder?.field2)) {
              const errorMsg = `Tab: ${tab.name} has a form component without ${!builder?.field1 ? "field1" : "field2"}, kindly input ${!builder?.field1 ? "field1" : "field2"} for all components in ${tab.name}`;
              acc = {
                error: errorMsg,
                tabIndex: index,
              };
              const newTabs = handleBuildChange({
                buildId: builder.id,
                tabId: tab.id,
                value: errorMsg,
                key: "error",
              });

              setTab(newTabs);
              return acc;
            }
      
            if (["radio", "checkbox", "dropdown"].includes(builder?.type) && 
            builder?.options?.some(
              (option: { option: string }) => option?.option === ""
            )) {
              const errorMsg = `Tab: ${tab.name} has a form component without options, kindly input options for all components in ${tab.name}`;
              acc = {
                error: errorMsg,
                tabIndex: index,
              };
              const newTabs = handleBuildChange({
                buildId: builder.id,
                tabId: tab.id,
                value: errorMsg,
                key: "error",
              });

              setTab(newTabs);
              return acc;
            }
          });
          return acc;
        },
        {}
      );
      if (builderCheck.error) {
        return builderCheck;
      }
      return false;
    };

    const handleSubmit = (e: React.SyntheticEvent) => {
      e.preventDefault();

      const invalidOptions = tabs
        ?.flatMap((tab) =>
          tab?.builders?.filter(
            (builder) =>
            ["radio", "checkbox","dropdown"].includes(builder?.type) &&
              builder?.options?.some(
                (option: { option: string }) => option?.option === ""
              )
          )
        )
        ?.flatMap((builder) =>
          builder.options.filter(
            (option: { option: string }) => option.option === ""
          )
        );

        
        const  invalidInputList = tabs
        ?.flatMap((tab) =>
          tab?.builders?.filter(
            (builder) =>
              builder?.type === "listInputs" &&
            (builder?.field1 === "" || builder?.field2 === "")
          )
        );

     
      const error = validateTabs(tabs);
      if (error) {
        setTabIndex(error.tabIndex);
        onSubmit(error);
      } else  if (tabs[0].name === "New Form" && tabs[0].builders.length === 0||
      invalidOptions?.length || invalidInputList?.length ) {
        onSubmit([]);
        return;
      } else {
        onSubmit(tabs);
      }
    };

    const handleChangeName = (
      e: React.ChangeEvent<HTMLInputElement>,
      id: string
    ) => {
      const { value } = e.target;
      const newTabs = tabs.map((tab) => {
        if (tab?.id === id) {
          tab.name = value;
          tab.error = "";
        }

        return tab;
      });
      setTab(newTabs);
    };

    const handleTabsChange = (index: number) => {
      setTabIndex(index);
    };

    const addNewTab = (e: React.MouseEvent) => {
      e.preventDefault();
      setTab([...tabs, { id: nanoid(), name: "New Form", builders: [] }]);
      setTabIndex(tabs.length);
    };

    useImperativeHandle(
      ref,
      () => ({
        togglePreview: togglePreview,
        getFormData: () => tabs,
      }),
      [tabs]
    );

    const deleteTab = (id: string, i: number) => {
      const newTabs = tabs.filter((tab) => tab.id !== id);
      newTabs.length === 0
        ? setTab([{ id: nanoid(), name: "New Form", builders: [] }])
        : setTab(newTabs);
      setTabIndex(i !== 0 ? i - 1 : 0);
    };

    const handleBuildChange = ({ buildId, tabId, value, key }: BuildChange) => {
      const newTabs = tabs.map((tab) => {
        if (tab?.id === tabId) {
          tab.builders = tab.builders.map((builder) => {
            if (builder?.id === buildId) {
              builder[key] = value;
              if (key === "instruction") builder.error = "";
            }
            return builder;
          });
        }

        return tab;
      });
      return newTabs;
    };

    const deleteBuild = (id: string, tabId: string) => {
      const newTabs = tabs.map((tab) => {
        if (tab?.id === tabId) {
          tab.builders = tab.builders?.filter((item) => item?.id !== id);
        }

        return tab;
      });
      setTab(newTabs);
    };

    const handleChangeInstruction = (
      e: React.ChangeEvent<HTMLInputElement>,
      buildId: string,
      tabId: string
    ) => {
      const { value } = e.target;

      const newTabs = handleBuildChange({
        buildId,
        tabId,
        value,
        key: "instruction",
      });
      setTab(newTabs);
    };

    const toggleRequired = (
      e: React.ChangeEvent<HTMLInputElement>,
      buildId: string,
      tabId: string
    ) => {
      const { checked } = e.target;

      const newTabs = handleBuildChange({
        buildId,
        tabId,
        value: checked,
        key: "required",
      });
      setTab(newTabs);
    };

    const handleModalConfirm = () => {
      deleteTab(current.id, current.index);
      onClose();
    };

    const updateCurrent = (id: string, index: number) => {
      setCurrent({ id, index });
    };

    const addOptions = (tabId: string, id: string) => {
      const newTabs = tabs.map((tab) => {
        if (tab.id === tabId) {
          const updatedBuilders = tab.builders.map((builder) => {
            if (builder.id === id) {
              return {
                ...builder,
                options: [
                  ...builder.options,
                  {
                    id: nanoid(),
                    option: `option ${builder.options.length + 1}`,
                    linked: false,
                    linkedForm: "",
                  },
                ],
              };
            }
            return builder;
          });
          return { ...tab, builders: updatedBuilders };
        }
        return tab;
      });
      setTab(newTabs);
    };

    const editOption = (
      tabId: string,
      builderId: string,
      optionId: string,
      e: React.ChangeEvent<HTMLInputElement>,
      link?: string,
      linkedForm?: any
    ) => {
      const { value } = e.target;
      let linkedFormId = '';
      const newTabs = tabs.map((tab) => {
        const linked = link === "link";
        if (tab?.name === linkedForm?.name) {
          tab.linkedToOption = linked;
          linked && (linkedFormId = linkedForm.id);
        }
        if (tab.id === tabId) {
          const updatedBuilders = tab.builders.map((builder) => {
            if (builder.id === builderId) {
              const updatedOptions = builder.options.map((option: any) => {
                if (option.id === optionId) {
                  return link
                    ? { ...option, id: nanoid(), linked, linkedFormId, linkedForm: linked ? linkedForm : '' }
                    : { ...option, id: nanoid(), linkedFormId, option: value };
                }
                return option;
              });
              return { ...builder, options: updatedOptions };
            }
            return builder;
          });

          return { ...tab, builders: updatedBuilders };
        }
        return tab;
      });
      setTab(newTabs);
    };

    const deleteOption = (tabId: string, id: string, index: number) => {
      const newTabs = tabs.map((tab) => {
        if (tab.id === tabId) {
          const updatedBuilders = tab.builders.map((builder) => {
            if (builder.id === id) {
              const updatedOptions = [...builder.options];
              updatedOptions.splice(index, 1);
              return { ...builder, options: updatedOptions };
            }
            return builder;
          });
          return { ...tab, builders: updatedBuilders };
        }
        return tab;
      });
      setTab(newTabs);
    };

    const TabdragStart = (e: React.DragEvent, tab: BuilderTabs) => {
      e.dataTransfer.setData("tab", JSON.stringify(tab));
    };

    const handleTabDragEnter = (e: React.DragEvent, tab: BuilderTabs) => {
      e.preventDefault();
      e.stopPropagation();
      setOverTab(tab);
    };

    const handleTabDrop = (e: React.DragEvent) => {
      const dragTab = JSON.parse(e.dataTransfer.getData("tab"));
      const index = tabs.findIndex((i) => i.id === overTab.id);
      const newTab = tabs.filter((b) => b.id !== dragTab.id);
      newTab.splice(index, 0, dragTab);
      setTab(newTab);
    };

    const cssVarsRoot = nanoid();

    const editInputList = (
      tabId: string,
      builderId: string,
      e: React.ChangeEvent<HTMLInputElement>,
      field: string
    ) => {
      const { value } = e.target;
      const newTabs = tabs.map((tab) => {
        if (tab.id === tabId) {
          const updatedBuilders = tab.builders.map((builder) => {
            if (builder.id === builderId) {
              return { ...builder, [field]: value };
            }
            return builder;
          });
          return { ...tab, builders: updatedBuilders };
        }
        return tab;
      });
      setTab(newTabs);
    };

    return (
      <ChakraProvider cssVarsRoot={`#${cssVarsRoot}`} theme={theme}>
        <ConfirmationModal
          isOpen={isOpen}
          onClose={onClose}
          handleModalConfirm={handleModalConfirm}
        />
        <FormPreview
          isOpen={previewOpen}
          onClose={togglePreview}
          tabs={tabs}
          // setTabs={setPreviewTab}
        />
        <Box id={cssVarsRoot}>
          <Box as={"form"} width={"100%"} onSubmit={handleSubmit}>
            <Tabs
              onChange={handleTabsChange}
              index={tabIndex}
              size="lg"
              mt={"1"}
              variant="enclosed"
              colorScheme="blue"
            >
              <Flex gap={4} width={"100%"} alignItems="center">
                <TabList
                  overflowX="auto"
                  css={css({
                    scrollbarWidth: "none",
                    "::-webkit-scrollbar": { display: "none" },
                    "-webkit-overflow-scrolling": "touch",
                    boxShadow: "inset 0 -2px 0 rgba(0, 0, 0, 0.1)",
                    border: "0 none",
                  })}
                >
                  {tabs?.map((tab, i) => (
                    <Flex
                      alignItems="center"
                      border={tabIndex === i ? "1px solid #2B6CB0" : ""}
                      color={tabIndex === i ? "#2B6CB0" : "black"}
                      borderBottom="none"
                      px={2}
                      key={tab.id}
                    >
                      <Tab
                        px={0}
                        onClick={() => {
                          setTabIndex(i);
                          if (kyc && tab.name === 'Minimum Requirement') {
                            setMinimumTab(true)
                          } else {
                            setMinimumTab(false)
                          }
                        }}
                        onDrop={handleTabDrop}
                        onDragOver={(e) => handleTabDragEnter(e, tab)}
                        onDragStart={(e) => TabdragStart(e, tab)}
                        _focus={{ border: "none" }}
                        _selected={{ border: "none" }}
                      >
                        <Text width={"fit-content"} mx={0}>
                          {tab.name}
                        </Text>
                      </Tab>
                      {!kyc && (
                        <IconButton
                          _focus={{ outline: "none" }}
                          size="xs"
                          variant="ghost"
                          aria-label=""
                          onClick={() => {
                            updateCurrent(tab.id, i);
                            onOpen();
                          }}
                          icon={<CloseIcon w={4} h={4} />}
                        />
                      )}
                    </Flex>
                  ))}
                </TabList>
                {!kyc && (
                  <IconButton
                    _focus={{ outline: "none" }}
                    size="xs"
                    variant="ghost"
                    aria-label=""
                    onClick={addNewTab}
                    icon={<PlusIcon w={6} h={6} />}
                  />
                )}
              </Flex>

              <Flex borderBottom="1px solid" borderColor="gray.300">
                <Box pt="3" ps="0" pe="1" width={"66%"}>
                  <TabPanels height={"63vh"} overflowY={"scroll"}>
                    {tabs?.map((tab, i) => (
                      <TabPanel key={tab.id}>
                        <Box minHeight={"50vh"} pb="41px">
                          <FormHeader
                            kyc={kyc}
                            tab={tab}
                            updateCurrent={updateCurrent}
                            addNewTab={addNewTab}
                            handleChangeName={handleChangeName}
                            index={i}
                            onOpen={onOpen}
                          />
                          <Box
                            width={"100%"}
                            minHeight={"50vh"}
                            overflowY={"scroll"}
                            onDrop={(e) => onDrop(e, tab.id)}
                            onDragOver={(e) => handleDragEnter(e, tab.id)}
                          >
                            <FormBuilderBoard
                              deleteOption={deleteOption}
                              editOption={editOption}
                              builders={tab.builders}
                              deleteBuild={deleteBuild}
                              tab={tab}
                              kyc={kyc}
                              handleChangeInstruction={handleChangeInstruction}
                              toggleRequired={toggleRequired}
                              reOrderTabBuilders={reOrderTabBuilders}
                              toggleReorder={toggleReorder}
                              addOptions={addOptions}
                              tabs={tabs}
                              editInputList={editInputList}
                            />
                          </Box>
                        </Box>
                      </TabPanel>
                    ))}
                  </TabPanels>
                </Box>
                <Box width={"34%"} height="60vh">
                  <Box
                    bg="blackAlpha.50"
                    overflowY="scroll"
                    height="60vh"
                    p="35px"
                    pt="10px"
                  >
                    <BuildersList
                      minimumTab={minimumTab}
                      list={minimumTab? []: list}
                      handleDragStart={handleDragStart}
                      kyc={kyc}
                    />
                  </Box>
                </Box>
              </Flex>
            </Tabs>
            {children}
          </Box>
        </Box>
      </ChakraProvider>
    );
  }
);

export default FormBuilder;
